import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, CircularProgress, Paper, styled } from "@mui/material";
import { type ReactNode } from "react";

/** Default width for `layout="fixed"` (task dashboard strip uses the same value for virtual columns). */
export const TASK_CARD_FIXED_WIDTH_PX = 300;

interface TaskCardProps {
  isSelected?: boolean;
  onClick?: () => void;
  onComplete?: (event: React.MouseEvent) => void;
  isCompleting?: boolean;
  /**
   * `fluid` fills the horizontal space (e.g. narrow containers). `fixed` uses {@link TASK_CARD_FIXED_WIDTH_PX}.
   */
  layout?: "fixed" | "fluid";
  children: ReactNode;
}

const StyledTaskCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "layout",
})<{ isSelected?: boolean; layout?: "fixed" | "fluid" }>(({ theme, isSelected, layout = "fixed" }) => ({
  "&::before": isSelected
    ? {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "50%",
        content: '""',
        height: "10px",
        position: "absolute",
        right: "10px",
        top: "10px",
        width: "10px",
      }
    : {},

  "&:hover": {
    boxShadow: theme.shadows[5],
    transform: isSelected ? "scale(1.02)" : "scale(1.01)",
  },

  backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,

  borderLeft: isSelected ? `5px solid ${theme.palette.primary.main}` : "none",

  boxShadow: isSelected ? theme.shadows[8] : theme.shadows[1],

  cursor: "pointer",

  // Center the card within its container
  display: "flex",

  flexDirection: "column",

  height: "280px",

  padding: theme.spacing(2.5),

  position: "relative",

  transform: isSelected ? "scale(1.02)" : "scale(1)",

  transition: "all 0.3s ease",
  ...(layout === "fluid"
    ? {
        boxSizing: "border-box",
        margin: 0,
        maxWidth: "100%",
        minWidth: 0,
        width: "100%",
      }
    : {
        margin: "0 auto",
        width: `${TASK_CARD_FIXED_WIDTH_PX}px`,
      }),
}));

export const TaskCard = ({
  isSelected,
  onClick,
  onComplete,
  isCompleting = false,
  layout = "fixed",
  children,
}: TaskCardProps) => {
  const handleCompleteClick = (event: React.MouseEvent) => {
    // Stop propagation to prevent the card click event from firing
    event.stopPropagation();
    if (onComplete && !isCompleting) {
      onComplete(event);
    }
  };

  return (
    <StyledTaskCard isSelected={isSelected} layout={layout} onClick={onClick}>
      {children}

      {/* Complete button - positioned at the bottom right */}
      {onComplete && (
        <Box
          sx={{
            alignItems: "center",
            bottom: "16px",
            display: "flex",

            // Ensure vertical centering
            height: "32px",

            position: "absolute",

            // Match the footer spacing
            right: "16px",
            zIndex: 2, // Match the height we set for the due date
          }}
        >
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={!isCompleting && <CheckCircleOutlineIcon />}
            onClick={handleCompleteClick}
            disabled={isCompleting}
            sx={{
              "&:hover": {
                backgroundColor: "success.light",
                color: "common.white",
              },
              borderRadius: "20px",
              minWidth: "auto",
            }}
          >
            {isCompleting ? (
              <Box sx={{ alignItems: "center", display: "flex" }}>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Completing...
              </Box>
            ) : (
              "Complete"
            )}
          </Button>
        </Box>
      )}
    </StyledTaskCard>
  );
};
